import {useConfig} from '@aboutyou/config';
import {Image} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/image.types';
import {SpotlightSection_Element} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  BackgroundCard as BackgroundCardUI,
  COLORS,
  tabletAndUpLinaria,
  useFullWidthSmallImage,
} from '@aboutyou/ui-components';
import {getColorShade} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC, useMemo} from 'react';
import DealBackgroundDarkLandscape from './assets/deal-background-dark-landscape.svg';
import DealBackgroundDark from './assets/deal-background-dark.svg';
import DealBackgroundLightLandscape from './assets/deal-background-light-landscape.svg';
import DealBackgroundLight from './assets/deal-background-light.svg';

export const BackgroundCard: FC<
  SpotlightSection_Element & {
    isActive: boolean;
  }
> = ({type, isActive}) => {
  const {device} = useConfig();
  const isMobile = device === 'mobile';
  const LightSVGImage = isMobile
    ? DealBackgroundLight
    : DealBackgroundLightLandscape;
  const DarkSVGImage = isMobile
    ? DealBackgroundDark
    : DealBackgroundDarkLandscape;

  const cardData: {
    scale: number;
    blur: number;
    color?: string;
    linearGradient?: string;
    image?: Image;
    svgImage?: string;
  } = useMemo(() => {
    const linearGradient =
      'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 100%, rgba(0, 0, 0, 0.16) 0%), rgba(0, 0, 0, 0.10)';

    switch (type?.$case) {
      case 'teaser':
        return {
          scale: 2.5,
          blur: 50,
          image: isMobile
            ? type.teaser.coverSquareImage
            : type.teaser.coverLandscapeImage,
          linearGradient,
        };
      case 'campaign': {
        const colorShade = getColorShade(type.campaign.backgroundColor.hex);

        return {
          scale: isMobile ? 2.5 : 1.1,
          blur: 10,
          color: type.campaign.backgroundColor.hex,
          // we use the opposite color from the given shade to get contrast
          svgImage: colorShade === 'dark' ? LightSVGImage : DarkSVGImage,
        };
      }
      case 'highTierCoupon': {
        const colorShade = getColorShade(
          type.highTierCoupon.backgroundColor.hex,
        );

        return {
          scale: isMobile ? 2.5 : 1.1,
          blur: 10,
          color: type.highTierCoupon.backgroundColor.hex,
          // we use the opposite color from the given shade to get contrast
          svgImage: colorShade === 'dark' ? LightSVGImage : DarkSVGImage,
        };
      }
      case 'content':
        return {
          scale: 2.5,
          blur: 50,
          image: isMobile
            ? type.content.coverSquareImage
            : type.content.coverLandscapeImage,
          color: type.content.backgroundColorSquare?.hex,
          linearGradient,
        };
      default:
        return {
          scale: 2.5,
          blur: 50,
          image: undefined,
          linearGradient,
        };
    }
  }, [type]);

  const backgroundImageUrl = useFullWidthSmallImage({
    ratio: '1:1',
    src: cardData && cardData.image ? cardData.image.url : '',
  });

  return (
    <StyledBackgroundCardUI
      isActive={isActive}
      imageUrl={cardData.svgImage ? cardData.svgImage : backgroundImageUrl}
      color={backgroundImageUrl === '' ? COLORS['Fill.Black'] : cardData?.color}
      linearGradient={cardData?.linearGradient}
      blur={cardData.blur}
      scale={cardData.scale}
    />
  );
};

const StyledBackgroundCardUI = styled(BackgroundCardUI)`
  ${tabletAndUpLinaria(`
    background-size: cover;
  `)}

  &::after {
    content: '';
    background: rgba(0, 0, 0, 0.2);
    position: absolute;
    inset: 0;
  }
`;
